/**
 * ReviewPanel — the right-hand pane of the editor's Comment mode.
 *
 * Top: the composer for the pin you just dropped (draft). Below: every
 * comment on this page, open ones first. The active thread expands into its
 * conversation with reply / resolve / reopen / delete. Clicking a thread
 * (or its pin in the preview) makes it active — the editor then switches the
 * preview to the screen size the pin was dropped at and scrolls to it.
 *
 * Presentation only: PagesEditor owns the state (useReview) and passes
 * handlers in. docs/REVIEW.md has the whole flow.
 */

import { useEffect, useRef, useState } from 'react';
import { SECTION_META, type SectionType } from '@vv/sections';
import {
  VIEWPORT_LABEL,
  ago,
  authorName,
  pinLabel,
  type ReviewAnchor,
  type ReviewDraft,
  type ReviewKind,
  type ReviewStatus,
  type ReviewThread,
  type ReviewViewport,
} from '~/lib/review';

interface Me {
  id: string;
  email: string;
  role: 'dev' | 'admin' | 'editor';
}

interface Props {
  viewport: ReviewViewport;
  threads: ReviewThread[];
  draft: ReviewDraft | null;
  activeId: string | null;
  showResolved: boolean;
  onShowResolved: (v: boolean) => void;
  me: Me;
  loading: boolean;
  error: string | null;
  onActivate: (id: string | null) => void;
  onSubmitDraft: (body: string, kind: ReviewKind) => Promise<void>;
  onCancelDraft: () => void;
  onReply: (id: string, body: string) => Promise<void>;
  onShip: (id: string, body: string) => Promise<void>;
  onSetStatus: (id: string, status: ReviewStatus) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onEditSection?: (t: ReviewThread) => void;
  onNotifyNow: () => Promise<{ sent: boolean; comments: number; reason?: string }>;
}

/** "Service cards · “Hydration Boost…”" — what a pin points at. */
export function describeAnchor(a: ReviewAnchor | null): string {
  if (!a) return '';
  const where = a.chrome
    ? a.chrome === 'nav'
      ? 'Menu'
      : a.chrome === 'footer'
        ? 'Footer'
        : a.chrome
    : a.section
      ? (SECTION_META[a.section.type as SectionType]?.label ?? a.section.type)
      : 'Page';
  const text = a.text.length > 56 ? `${a.text.slice(0, 55)}…` : a.text;
  return text ? `${where} · “${text}”` : where;
}

const QUIET_MS = 30 * 60_000;

export default function ReviewPanel({
  viewport,
  threads,
  draft,
  activeId,
  showResolved,
  onShowResolved,
  me,
  loading,
  error,
  onActivate,
  onSubmitDraft,
  onCancelDraft,
  onReply,
  onShip,
  onSetStatus,
  onDelete,
  onEditSection,
  onNotifyNow,
}: Props) {
  const isDev = me.role === 'dev';
  const openCount = threads.filter((t) => t.status === 'open').length;
  const resolvedCount = threads.length - openCount;
  const shown = threads
    .filter((t) => showResolved || t.status === 'open' || t.id === activeId)
    .sort(
      (a, b) =>
        (a.status === b.status ? 0 : a.status === 'open' ? -1 : 1) ||
        (a.kind === b.kind ? 0 : a.kind === 'change' ? -1 : 1) ||
        a.number - b.number,
    );

  // "Send now" is for reviewers whose last comment is still inside the quiet
  // window — i.e. the team hasn't been emailed yet.
  const recentOwn = !isDev
    ? threads.some((t) =>
        t.comments.some(
          (c) => c.author_id === me.id && Date.now() - Date.parse(c.created_at) < QUIET_MS,
        ),
      )
    : false;
  const [notifyState, setNotifyState] = useState<'idle' | 'busy' | 'sent' | 'already' | 'error'>('idle');

  return (
    <div className="rv-panel">
      <header className="rv-panel__head">
        <h2>Comments</h2>
        <span className="rv-chip">{VIEWPORT_LABEL[viewport]} view</span>
      </header>

      <div className="rv-panel__body">
        {draft ? (
          <Composer
            // One composer per draft, NOT per click: a stray click on the page
            // moves the pin but keeps what was typed.
            key="draft"
            draft={draft}
            isDev={isDev}
            onSubmit={onSubmitDraft}
            onCancel={onCancelDraft}
          />
        ) : (
          <p className="rv-hint">
            Click anywhere on the page to leave a comment. Each one gets a number, and remembers
            which screen size you were on.
          </p>
        )}

        {error && <p className="rv-error">{error}</p>}

        <div className="rv-filter">
          <span>
            {openCount} open
            {resolvedCount > 0 && ` · ${resolvedCount} resolved`}
          </span>
          {resolvedCount > 0 && (
            <label>
              <input
                type="checkbox"
                checked={showResolved}
                onChange={(e) => onShowResolved(e.target.checked)}
              />{' '}
              Show resolved
            </label>
          )}
        </div>

        {loading && threads.length === 0 ? (
          <p className="rv-hint">Loading comments…</p>
        ) : shown.length === 0 ? (
          <p className="rv-empty">
            {threads.length === 0 ? 'No comments on this page yet.' : 'Nothing open on this page.'}
          </p>
        ) : (
          <ul className="rv-list">
            {shown.map((t) => (
              <ThreadRow
                key={t.id}
                t={t}
                me={me}
                viewport={viewport}
                active={t.id === activeId}
                onActivate={onActivate}
                onReply={onReply}
                onShip={onShip}
                onSetStatus={onSetStatus}
                onDelete={onDelete}
                onEditSection={onEditSection}
              />
            ))}
          </ul>
        )}

        {recentOwn && (
          <div className="rv-notify">
            <p>
              The team is emailed about your comments 30 minutes after your last one. Finished
              sooner?
            </p>
            <button
              type="button"
              className="rv-btn"
              disabled={notifyState === 'busy' || notifyState === 'sent' || notifyState === 'already'}
              onClick={async () => {
                setNotifyState('busy');
                try {
                  const r = await onNotifyNow();
                  // The API answers 200 even when nothing went out — believe
                  // its `sent`, not the HTTP status.
                  setNotifyState(r.sent ? 'sent' : r.reason === 'nothing' ? 'already' : 'error');
                } catch {
                  setNotifyState('error');
                }
              }}
            >
              {notifyState === 'sent'
                ? 'Sent — the team has been emailed'
                : notifyState === 'already'
                  ? 'Already sent — nothing new to send'
                  : notifyState === 'busy'
                    ? 'Sending…'
                    : 'Send to the team now'}
            </button>
            {notifyState === 'error' && (
              <p className="rv-error">Couldn’t send just now. It will still go out automatically.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Composer({
  draft,
  isDev,
  onSubmit,
  onCancel,
}: {
  draft: ReviewDraft;
  isDev: boolean;
  onSubmit: (body: string, kind: ReviewKind) => Promise<void>;
  onCancel: () => void;
}) {
  const [body, setBody] = useState('');
  const [kind, setKind] = useState<ReviewKind>('change');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const ref = useRef<HTMLTextAreaElement | null>(null);
  useEffect(() => ref.current?.focus(), []);

  async function submit() {
    const text = body.trim();
    if (!text || busy) return;
    setBusy(true);
    setErr(null);
    try {
      await onSubmit(text, kind);
    } catch (e) {
      setErr((e as Error).message);
      setBusy(false);
    }
  }

  return (
    <section className={`rv-composer${kind === 'dev' ? ' is-dev' : ''}`} aria-label="New comment">
      <div className="rv-composer__where">
        <span className="rv-pin rv-pin--draft" aria-hidden>
          +
        </span>
        <div>
          <strong>New comment</strong>
          <div className="rv-muted">
            {VIEWPORT_LABEL[draft.viewport]} view · {describeAnchor(draft.anchor) || 'this spot'}
          </div>
        </div>
      </div>

      {isDev && (
        <div className="rv-seg" role="group" aria-label="Type of comment">
          <button
            type="button"
            aria-pressed={kind === 'change'}
            onClick={() => setKind('change')}
          >
            Change request
          </button>
          <button type="button" aria-pressed={kind === 'dev'} onClick={() => setKind('dev')}>
            Dev note
          </button>
        </div>
      )}
      {kind === 'dev' && (
        <p className="rv-muted">Only the dev team (and Claude) can see dev notes. Clients never do.</p>
      )}

      <textarea
        ref={ref}
        id="rv-draft-body"
        className="rv-textarea"
        rows={4}
        value={body}
        placeholder={kind === 'dev' ? 'Note for the dev team…' : 'What should change here?'}
        onChange={(e) => setBody(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onCancel();
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) void submit();
        }}
        maxLength={4000}
      />
      <p className="rv-muted">Please don’t include patient names or health details.</p>
      {err && <p className="rv-error">{err}</p>}
      <div className="rv-actions">
        <button type="button" className="rv-btn rv-btn--primary" disabled={!body.trim() || busy} onClick={submit}>
          {busy ? 'Saving…' : 'Add comment'}
        </button>
        <button type="button" className="rv-btn" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </section>
  );
}

function ThreadRow({
  t,
  me,
  viewport,
  active,
  onActivate,
  onReply,
  onShip,
  onSetStatus,
  onDelete,
  onEditSection,
}: {
  t: ReviewThread;
  me: Me;
  viewport: ReviewViewport;
  active: boolean;
  onActivate: (id: string | null) => void;
  onReply: (id: string, body: string) => Promise<void>;
  onShip: (id: string, body: string) => Promise<void>;
  onSetStatus: (id: string, status: ReviewStatus) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onEditSection?: (t: ReviewThread) => void;
}) {
  const rowRef = useRef<HTMLLIElement | null>(null);
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (active) rowRef.current?.scrollIntoView({ block: 'nearest' });
  }, [active]);
  useEffect(() => {
    if (!confirmDelete) return;
    const id = window.setTimeout(() => setConfirmDelete(false), 4000);
    return () => window.clearTimeout(id);
  }, [confirmDelete]);

  const first = t.comments[0];
  const canResolve = me.role === 'dev' || me.role === 'admin' || t.created_by === me.id;
  const canDelete = me.role === 'dev' || t.created_by === me.id;
  const canShip = me.role === 'dev' || me.role === 'admin';
  const otherSize = t.viewport !== viewport;

  async function run(fn: () => Promise<void>) {
    setBusy(true);
    setErr(null);
    try {
      await fn();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <li
      ref={rowRef}
      className={`rv-thread${active ? ' is-active' : ''}${t.status === 'resolved' ? ' is-resolved' : ''}`}
    >
      <button
        type="button"
        className="rv-thread__head"
        aria-expanded={active}
        onClick={() => onActivate(active ? null : t.id)}
      >
        <span
          className={`rv-pin${t.kind === 'dev' ? ' rv-pin--dev' : ''}${t.status === 'resolved' ? ' rv-pin--done' : ''}`}
          aria-hidden
        >
          {t.status === 'resolved' ? '✓' : pinLabel(t)}
        </span>
        <span className="rv-thread__text">
          <span className="rv-thread__snippet">{first?.body ?? '(no text)'}</span>
          <span className="rv-muted">
            {authorName(t.created_by_email, me.email)} · {ago(t.created_at)}
            {t.comments.length > 1 && ` · ${t.comments.length} comments`}
          </span>
        </span>
        <span className={`rv-chip${otherSize ? ' rv-chip--other' : ''}`} title={otherSize ? 'Click to switch to this screen size' : undefined}>
          {VIEWPORT_LABEL[t.viewport]}
        </span>
      </button>

      {active && (
        <div className="rv-thread__body">
          <p className="rv-muted">
            {t.kind === 'dev' ? 'Dev note · only the dev team sees this. ' : ''}
            {describeAnchor(t.anchor)}
          </p>
          <ol className="rv-comments">
            {t.comments.map((c) => (
              <li key={c.id} className={c.kind === 'shipped' ? 'is-shipped' : ''}>
                <div className="rv-comments__meta">
                  <strong>{c.kind === 'shipped' ? 'Shipped by ' : ''}{authorName(c.author_email, me.email)}</strong>
                  <span className="rv-muted">{ago(c.created_at)}</span>
                </div>
                <p>{c.body}</p>
              </li>
            ))}
          </ol>

          <textarea
            id={`rv-reply-${t.id}`}
            className="rv-textarea"
            rows={2}
            value={reply}
            placeholder={t.status === 'resolved' ? 'Not right yet? Reply to reopen…' : 'Reply…'}
            onChange={(e) => setReply(e.target.value)}
            maxLength={4000}
          />
          {err && <p className="rv-error">{err}</p>}
          <div className="rv-actions">
            <button
              type="button"
              className="rv-btn rv-btn--primary"
              disabled={!reply.trim() || busy}
              onClick={() =>
                run(async () => {
                  await onReply(t.id, reply.trim());
                  setReply('');
                })
              }
            >
              Reply
            </button>
            {t.status === 'open' && canResolve && (
              <button
                type="button"
                className="rv-btn"
                disabled={busy}
                onClick={() =>
                  run(async () => {
                    if (reply.trim() && canShip) await onShip(t.id, reply.trim());
                    else await onSetStatus(t.id, 'resolved');
                    setReply('');
                  })
                }
              >
                {reply.trim() && canShip ? 'Shipped ✓' : 'Resolve ✓'}
              </button>
            )}
            {t.status === 'resolved' && canResolve && (
              <button
                type="button"
                className="rv-btn"
                disabled={busy}
                onClick={() => run(() => onSetStatus(t.id, 'open'))}
              >
                Reopen
              </button>
            )}
            {onEditSection && t.anchor?.section && (
              <button type="button" className="rv-btn" onClick={() => onEditSection(t)}>
                Edit this section →
              </button>
            )}
            {canDelete && (
              <button
                type="button"
                className="rv-btn rv-btn--danger"
                disabled={busy}
                onClick={() => (confirmDelete ? void run(() => onDelete(t.id)) : setConfirmDelete(true))}
              >
                {confirmDelete ? 'Really delete?' : 'Delete'}
              </button>
            )}
          </div>
        </div>
      )}
    </li>
  );
}
